import { useAuth0 } from '@auth0/auth0-react';
import * as LucideIcons from 'lucide-react';
import { Image } from 'lucide-react';
import { marked } from 'marked';
import React, { useState } from 'react';
import { ListGroup, Modal } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
// @ts-ignore - SVG import handled by webpack
import EptBotSvg from '../../../images/ept_bot.svg';
import {
  AwsDealRoomDownloader,
  autoDownloadInit,
} from '../../common/AwsDealRoomDownloader';
import { showToast } from '../../common/ToastHandler';
import './MediaListBlock.css';

// Configure marked to handle line breaks properly
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

// TypeScript interfaces
interface MediaIcon {
  type: 'lucide' | 'url' | 'image';
  value: string;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  crop?: 'circle';
}

interface MediaItem {
  title?: string;
  description?: string;
  details?: string;
  clickable?: boolean;
  icon?: MediaIcon;
}

interface MediaListContent {
  items?: MediaItem[];
}

interface MediaListBlockData {
  items?: MediaItem[];
  content?: MediaListContent;
}

interface MediaListBlockProps {
  block?: MediaListBlockData;
  content?: MediaListContent;
}

interface RouteParams extends Record<string, string | undefined> {
  dealroom_id: string;
}

interface ImageErrorState {
  [key: number]: boolean;
}

interface IdTokenClaims {
  __raw: string;
}

// Function to preprocess content and convert Unicode bullets to markdown
const preprocessMarkdown = (text: string): string => {
  if (!text) return '';

  // Convert Unicode bullet points to markdown list items
  let processed = text.replace(/\u2022\s*/g, '- ');

  // Ensure proper line breaks for list items
  processed = processed.replace(/\n-/g, '\n\n-');

  // Handle multiple consecutive line breaks (convert to proper paragraph breaks)
  processed = processed.replace(/\n\n\n+/g, '\n\n');

  // Handle other common Unicode characters
  processed = processed.replace(/\u2013/g, '-'); // en dash
  processed = processed.replace(/\u2014/g, '--'); // em dash
  processed = processed.replace(/\u201c/g, '"'); // left double quote
  processed = processed.replace(/\u201d/g, '"'); // right double quote
  processed = processed.replace(/\u2019/g, "'"); // right single quote
  processed = processed.replace(/\u2018/g, "'"); // left single quote

  // Ensure list items have proper spacing
  processed = processed.replace(/^- /gm, '\n- ');
  processed = processed.replace(/^\n\n- /gm, '\n- ');

  return processed.trim();
};

const MediaListBlock: React.FC<MediaListBlockProps> = ({ block, content }) => {
  const { dealroom_id } = useParams<RouteParams>();
  const { getIdTokenClaims } = useAuth0();
  const location = useLocation();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [hoveredItem, setHoveredItem] = useState<MediaItem | null>(null);
  const [imageErrors, setImageErrors] = useState<ImageErrorState>({});

  // Handle both structures - items can come directly in block.items or in block.content.items
  const items: MediaItem[] =
    block?.items || block?.content?.items || content?.items || [];

  if (!items.length) {
    console.warn('No items found in MediaListBlock:', { block, content });
    return null;
  }

  const handleItemClick = (item: MediaItem): void => {
    if (item.clickable && item.details) {
      setSelectedItem(item);
      setShowModal(true);
    }
  };

  const getIconSize = (size?: string): number => {
    switch (size?.toLowerCase()) {
      case 'small':
        return 24;
      case 'large':
        return 64;
      case 'medium':
      default:
        return 48;
    }
  };

  const renderIcon = (icon: MediaIcon, itemIndex: number): React.ReactNode => {
    if (!icon) return null;

    if (icon.type === 'lucide') {
      // Special handling for Response Details: use custom bot image instead of lucide 'bot'
      if (
        (icon.value || '').toLowerCase() === 'bot' &&
        location.pathname.startsWith('/response/')
      ) {
        return (
          <img
            src={EptBotSvg}
            alt="EPT Bot"
            style={{
              width: getIconSize(icon.size),
              height: getIconSize(icon.size),
              borderRadius: 6,
            }}
          />
        );
      }

      // Convert kebab/snake/lowercase to PascalCase for lucide
      const iconName = icon.value
        .split(/[-_]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');

      const IconComponent = LucideIcons[
        iconName as keyof typeof LucideIcons
      ] as React.ComponentType<{
        size?: number;
        color?: string;
        strokeWidth?: number;
      }>;

      return IconComponent ? (
        <IconComponent
          size={getIconSize(icon.size)}
          color={icon.color || 'currentColor'}
          strokeWidth={1.5}
        />
      ) : (
        (() => {
          console.warn(`Lucide icon "${icon.value}" (${iconName}) not found`);
          return null;
        })()
      );
    }

    // Handle url/image type icons
    if (icon.type === 'url' || icon.type === 'image') {
      if (imageErrors[itemIndex]) {
        return (
          <Image
            size={getIconSize(icon.size)}
            color="currentColor"
            strokeWidth={1.5}
          />
        );
      }

      return (
        <img
          src={icon.value}
          alt=""
          className={`media-icon ${icon.crop === 'circle' ? 'rounded-circle' : ''}`}
          style={{
            width: getIconSize(icon.size),
            height: getIconSize(icon.size),
            objectFit: 'cover',
          }}
          onError={() =>
            setImageErrors(prev => ({ ...prev, [itemIndex]: true }))
          }
        />
      );
    }

    return null;
  };

  const handleDownload = async (
    linkText: string,
    dealroomId: string,
    reportId: string
  ): Promise<void> => {
    try {
      // Show starting toast
      showToast('info', `Preparing download for ${linkText}...`);

      // Get the ID token from Auth0
      const getidToken = (await getIdTokenClaims()) as IdTokenClaims;
      const idToken = getidToken.__raw;

      const presignedUrl = await AwsDealRoomDownloader(
        dealroomId,
        reportId,
        idToken
      );
      if (presignedUrl && presignedUrl !== 'error') {
        showToast('success', `Download started for ${linkText}`);
        await autoDownloadInit(presignedUrl, linkText);
      } else {
        showToast('error', `Failed to download ${linkText}. Please try again.`);
        console.error('Failed to get download URL');
      }
    } catch (error) {
      showToast('error', `Download failed for ${linkText}. Please try again.`);
      console.error('Error initiating download:', error);
    }
  };

  const transformTitle = (title: string): string => {
    if (!title) return '';

    // Debug: Log the title being processed
    // console.log('transformTitle called with:', title);

    // Match s3:// URLs with a report ID at the end
    const s3Match = title.match(/\[(.*?)\]\(s3:\/\/.*?\/(\d+)\)/);
    if (s3Match) {
      const [, linkText, reportId] = s3Match;
      const downloadId = `download-${reportId}-${Math.random()}`;

      // Add click handler after render
      setTimeout(() => {
        const element = document.getElementById(downloadId);
        if (element && dealroom_id) {
          element.onclick = (e: Event) => {
            e.preventDefault();
            handleDownload(linkText, dealroom_id, reportId);
          };
        }
      }, 0);

      return `<a href="#" id="${downloadId}" style="cursor: pointer;">${linkText}</a>`;
    }

    // Handle API URLs with deal_room_list pattern (flexible domain, only in markdown link format)
    const apiMatch = title.match(
      /\[(.*?)\]\(https:\/\/[^/]+\/v3\/deal_room_list\/(\d+)\/(\d+)\)/
    );
    // console.log('API match result:', apiMatch);
    if (apiMatch) {
      const [, linkText, , reportId] = apiMatch;
      const downloadId = `download-${reportId}-${Math.random()}`;

      // Add click handler after render
      setTimeout(() => {
        const element = document.getElementById(downloadId);
        if (element && dealroom_id) {
          element.onclick = (e: Event) => {
            e.preventDefault();
            handleDownload(linkText, dealroom_id, reportId);
          };
        }
      }, 0);

      return `<a href="#" id="${downloadId}" style="cursor: pointer;">${linkText}</a>`;
    }

    // Handle app.ept.ai/deal_room/ URLs
    const dealRoomMatch = title.match(
      /https:\/\/app\.ept\.ai\/deal_room\/(\d+)/
    );
    if (dealRoomMatch) {
      const [fullMatch, dealroomIdFromUrl] = dealRoomMatch;
      const downloadId = `download-${dealroomIdFromUrl}-${Math.random()}`;

      // Add click handler after render
      setTimeout(() => {
        const element = document.getElementById(downloadId);
        if (element && dealroom_id) {
          element.onclick = (e: Event) => {
            e.preventDefault();
            handleDownload('Download', dealroom_id, dealroomIdFromUrl);
          };
        }
      }, 0);

      // Transform just the URL portion while keeping the rest of the title intact
      return title.replace(
        fullMatch,
        `<a href="#" id="${downloadId}" style="cursor: pointer;">Download</a>`
      );
    }

    return title;
  };

  return (
    <>
      <ListGroup variant="flush">
        {items.map((item: MediaItem, index: number) => (
          <ListGroup.Item
            key={index}
            action={item.clickable}
            className="d-flex align-items-start gap-3 py-3 position-relative"
            onClick={() => handleItemClick(item)}
            onMouseEnter={() =>
              !item.clickable && item.details && setHoveredItem(item)
            }
            onMouseLeave={() => setHoveredItem(null)}
            style={{ cursor: item.clickable ? 'pointer' : 'default' }}
          >
            {item.icon && (
              <div
                className="media-icon-wrapper"
                style={{
                  minWidth: getIconSize(item.icon.size),
                  minHeight: getIconSize(item.icon.size),
                }}
              >
                {renderIcon(item.icon, index)}
              </div>
            )}
            <div className="media-content">
              <div className="media-title">
                <div
                  dangerouslySetInnerHTML={{
                    __html: marked(
                      preprocessMarkdown(transformTitle(item.title || ''))
                    ) as string,
                  }}
                />
              </div>
              {item.description && (
                <div
                  className="media-description text-muted chat-messages-wrap-content"
                  id={
                    location.pathname.startsWith('/response/') &&
                    (item.title || '').toLowerCase() === 'ept ai response'
                      ? 'response-answer-content'
                      : undefined
                  }
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: marked(
                        preprocessMarkdown(item.description)
                      ) as string,
                    }}
                  />
                </div>
              )}
            </div>
            {hoveredItem === item && item.details && (
              <div className="details-tooltip chat-messages-wrap-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: marked(preprocessMarkdown(item.details)) as string,
                  }}
                />
              </div>
            )}
          </ListGroup.Item>
        ))}
      </ListGroup>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        aria-labelledby="item-details-modal"
        centered
        className="item-details-modal"
      >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title id="item-details-modal" className="modal-title">
            {selectedItem && selectedItem.title && (
              <div
                dangerouslySetInnerHTML={{
                  __html: marked(
                    preprocessMarkdown(selectedItem.title)
                  ) as string,
                }}
              />
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {selectedItem && selectedItem.details && (
            <div className="item-details">
              <div className="markdown-content chat-messages-wrap-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: marked(
                      preprocessMarkdown(selectedItem.details)
                    ) as string,
                  }}
                />
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default MediaListBlock;
